import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, LOGO_URL } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Hammer, Layers, Sparkle } from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/8337527/pexels-photo-8337527.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1024&w=1536";
const SHOP_IMG =
  "https://images.unsplash.com/photo-1554178286-db408c69256a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const Runner = () => (
  <div aria-hidden="true" data-testid="section-runner" className="relative h-2.5 w-full overflow-hidden grain">
    <div className="absolute inset-0 bg-gradient-to-r from-amber-950 via-amber-700 to-amber-950" />
  </div>
);

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 4);
  const featuredOrAll = featured.length >= 2 ? featured : products.slice(0, 4);

  return (
    <div data-testid="home-page" className="bg-stone-950 text-stone-50">
      {/* HERO */}
      <section className="relative min-h-[calc(100svh-5rem)] sm:min-h-[calc(100svh-6rem)] flex items-end overflow-hidden grain">
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="dark wood grain"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-950/40 via-stone-950/60 to-stone-950" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16 pb-14 sm:pb-16 w-full">
          <p
            data-testid="hero-overline"
            className="text-overline text-amber-500 fade-up"
          >
            Hand-built · One of one · Solid hardwood
          </p>
          <h1
            data-testid="hero-title"
            className="font-display text-5xl sm:text-7xl lg:text-[8rem] font-black uppercase leading-[0.92] tracking-tighter mt-6 fade-up fade-up-delay-1"
          >
            Wood,
            <br />
            shaped by
            <br />
            <span className="text-amber-600">hand.</span>
          </h1>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-up fade-up-delay-2">
            <Link
              to="/shop"
              data-testid="hero-shop-btn"
              className="bg-amber-700 hover:bg-amber-600 text-stone-50 px-8 py-4 text-overline transition-colors flex items-center gap-3 w-fit"
            >
              Shop the work <ArrowRight size={16} />
            </Link>
            <Link
              to="/custom"
              data-testid="hero-custom-btn"
              className="border border-stone-50/30 hover:border-amber-500 hover:text-amber-500 text-stone-50 px-8 py-4 text-overline transition-colors flex items-center gap-3 w-fit"
            >
              Commission a build <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <Runner />
      <section className="bg-stone-950">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: Hammer, h: "Hand built", p: "Every piece cut, joined and finished by one set of hands — no mass production." },
            { icon: Layers, h: "Solid hardwood", p: "Walnut, cherry, hard maple, white oak. Real wood, real grain, real weight." },
            { icon: Sparkle, h: "One of one", p: "No two boards have the same figure. The piece you buy is the piece you see." },
          ].map(({ icon: Icon, h, p }) => (
            <div key={h} className="flex gap-5 items-start">
              <div className="w-12 h-12 border border-stone-800 flex items-center justify-center text-amber-600 shrink-0">
                <Icon size={20} strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-overline text-stone-500 mb-2">{h}</p>
                <p className="text-stone-300 leading-relaxed">{p}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED WORK — Bento */}
      <Runner />
      <section className="py-14 sm:py-16 bg-stone-950">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-overline text-amber-600 mb-4">For sale now</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                Recent work,
                <br />
                ready to ship.
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="featured-see-all"
              className="text-overline text-stone-400 hover:text-amber-500 flex items-center gap-2"
            >
              See everything <ArrowRight size={16} />
            </Link>
          </div>

          <div
            data-testid="featured-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {featuredOrAll.slice(0, 3).map((p, i) => (
              <ProductCard key={p.id} product={p} priority={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOM BUILDS CTA */}
      <Runner />
      <section className="relative py-14 sm:py-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={SHOP_IMG}
            alt="workshop"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-stone-950/80" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="text-overline text-amber-600 mb-4">Custom Builds</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Got something
              <br />
              in your head?
              <br />
              <span className="text-amber-600">Let's build it.</span>
            </h2>
            <p className="text-stone-300 mt-8 max-w-2xl text-lg leading-relaxed">
              Tables, boxes, cutting boards, signs, shelves, anything in
              hardwood. Send a message — sketches, photos, dimensions — and
              you'll get a personal reply with a quote and timeline.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/custom"
              data-testid="cta-inquire"
              className="bg-amber-700 hover:bg-amber-600 text-stone-50 px-8 py-5 text-overline flex items-center justify-between w-full transition-colors"
            >
              <span>Start your inquiry</span>
              <ArrowRight size={16} />
            </Link>
            <a
              href="mailto:woodbyzq@gmail.com"
              className="border border-stone-700 hover:border-amber-600 text-stone-50 hover:text-amber-500 px-8 py-5 text-overline flex items-center justify-between transition-colors"
            >
              <span>Or email directly</span>
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* MARK */}
      <Runner />
      <section className="py-16 text-center">
        <img
          src={LOGO_URL}
          alt="Wood by ZQ mark"
          className="h-32 w-32 mx-auto object-contain logo-invert opacity-90"
        />
        <p className="text-overline text-stone-500 mt-6">
          The mark of one set of hands.
        </p>
      </section>
    </div>
  );
}
